import { Col, Row } from 'antd';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { FC, useEffect, useMemo, useRef } from 'react';
import * as S from './WaterChartCentralAnalytic.styles';
import './WaterChartCentralAnalytic.styles.css';

interface WaterChartCentralAnalyticProps {
  normalWaterQuantity: number;
  warningWaterQuantity: number;
  dangerWaterQuantity: number;
  errorWaterQuantity: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleChartFilter: (indexClicked: number) => void;
}

export const WaterChartCentralAnalytic: FC<WaterChartCentralAnalyticProps> = (data) => {
  const chartRef = useRef<ApexCharts | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // calcula percentuais e prepara labels como string[]
  const { series, labels } = useMemo(() => {
    const n = data.normalWaterQuantity || 0;
    const w = data.warningWaterQuantity || 0;
    const d = data.dangerWaterQuantity || 0;
    const e = data.errorWaterQuantity || 0;

    const sum = n + w + d + e;
    const pct = (x: number) => (sum === 0 ? 0 : (x / sum) * 100);

    return {
      series: [pct(n), pct(w), pct(d), pct(e)],
      labels: [n, w, d, e].map(String), // <- AQUI: string[]
    };
  }, [data.normalWaterQuantity, data.warningWaterQuantity, data.dangerWaterQuantity, data.errorWaterQuantity]);

  // cria o chart uma única vez
  useEffect(() => {
    if (chartRef.current || !containerRef.current) return;

    const options: ApexOptions = {
      chart: {
        type: 'pie',
        width: '100%',
        height: 500,
        toolbar: { show: false },
        events: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dataPointSelection: (_event: MouseEvent, _context: any, config: any) =>
            config.selectedDataPoints[0].length == 0
              ? data.handleChartFilter(0)
              : data.handleChartFilter(config.dataPointIndex + 1),
        },
      },
      tooltip: { enabled: false },
      series, // number[]
      labels, // string[]
      colors: ['#096DD9', '#FAAD14', '#C41B24', '#6B25C6'],
      legend: {
        position: 'right',
        showForSingleSeries: true,
        horizontalAlign: 'center',
        fontSize: '14px',
        fontFamily: 'Mulish',
        fontWeight: 600,
        markers: { width: 32, height: 20, radius: 2 },
      },
      dataLabels: { enabled: false },
    };

    const chart = new ApexCharts(containerRef.current, options);
    chart.render();
    chartRef.current = chart;

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // atualiza quando os dados mudarem
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.updateOptions({ labels } as ApexOptions, false, true);
    chartRef.current.updateSeries(series, true);
  }, [series, labels]);

  return (
    <>
      <Row gutter={[8, 8]} style={{ width: '100%' }} justify={'space-between'}>
        <Col>
          <S.SubtitleRow>
            <S.SubtitleRectangle color="#096DD9" />
            <S.Subtitle>água central menor ou igual 30% da água retida</S.Subtitle>
          </S.SubtitleRow>
        </Col>
        <Col>
          <S.SubtitleRow>
            <S.SubtitleRectangle color="#FAAD14" />
            <S.Subtitle>água central entre 30%-70% da água retida</S.Subtitle>
          </S.SubtitleRow>
        </Col>
        <Col>
          <S.SubtitleRow>
            <S.SubtitleRectangle color="#C41B24" />
            <S.Subtitle>água central maior ou igual que 70% da água retida</S.Subtitle>
          </S.SubtitleRow>
        </Col>
        <Col>
          <S.SubtitleRow>
            <S.SubtitleRectangle color="#6B25C6" />
            <S.Subtitle>outros eventos</S.Subtitle>
          </S.SubtitleRow>
        </Col>
      </Row>

      <Row justify={'center'} style={{ width: '100%', marginTop: '2.5rem', marginBottom: '1.5rem' }}>
        <Col>
          <div id="water_pie_chart" ref={containerRef} />
        </Col>
      </Row>
    </>
  );
};
